import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  Button,
  Input,
  Modal,
  StatusBadge,
  uiCurrency,
} from '@besiktning/ui';
import {
  Plus,
  Pencil,
  Trash2,
  Wrench,
  Image as ImageIcon,
  X,
} from 'lucide-react';
import { garageApi, carCareApi, formatCurrency } from '@besiktning/ui';
import type { Service, ServiceCategory, CarCareServiceCategory } from '@besiktning/ui';
import { SERVICE_CATEGORIES, CAR_CARE_SERVICE_CATEGORIES } from '@besiktning/ui';
import { useAuthStore } from '../store/authStore';
import { t } from '../i18n';

type AnyServiceCategory = ServiceCategory | CarCareServiceCategory;

interface ServiceFormState {
  name: string;
  description: string;
  price: string;
  category: AnyServiceCategory;
  duration_minutes: string;
  active: boolean;
  image: File | null;
  imagePreview: string | null;
}

const initialFormState: ServiceFormState = {
  name: '',
  description: '',
  price: '',
  category: 'general',
  duration_minutes: '',
  active: true,
  image: null,
  imagePreview: null,
};

export default function Services() {
  const { currentMembership } = useAuthStore()
  const isCarCare = currentMembership?.type === 'car_care'
  const api = isCarCare ? carCareApi : garageApi
  const categories = isCarCare ? CAR_CARE_SERVICE_CATEGORIES : SERVICE_CATEGORIES
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [form, setForm] = useState<ServiceFormState>({...initialFormState, category: categories[0].value});
  const [categoryFilter, setCategoryFilter] = useState<AnyServiceCategory | ''>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: services = [], isLoading } = useQuery({
    queryKey: ['services', isCarCare ? 'car_care' : 'garage', categoryFilter],
    queryFn: () =>
      api.getServices(
        categoryFilter ? { category: categoryFilter } : undefined
      ),
  });

  const createMutation = useMutation({
    mutationFn: (data: {
      name: string;
      description: string;
      price: number | null;
      category: AnyServiceCategory;
      duration_minutes: number | null;
      active: boolean;
      image: File | null;
    }) => api.createService(data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      closeModal();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: {
        name: string;
        description: string;
        price: number | null;
        category: AnyServiceCategory;
        duration_minutes: number | null;
        active: boolean;
        image: File | null;
      };
    }) => api.updateService(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteService(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });

  const openCreateModal = () => {
    setEditingService(null);
    setForm(initialFormState);
    setShowModal(true);
  };

  const openEditModal = (service: Service) => {
    setEditingService(service);
    setForm({
      name: service.name,
      description: service.description || '',
      price: service.price?.toString() || '',
      category: service.category,
      duration_minutes: service.duration_minutes?.toString() || '',
      active: service.active,
      image: null,
      imagePreview: service.image_url || null,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingService(null);
    setForm(initialFormState);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setForm({
        ...form,
        image: file,
        imagePreview: URL.createObjectURL(file),
      });
    }
  };

  const clearImage = () => {
    setForm({
      ...form,
      image: null,
      imagePreview: null,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data = {
      name: form.name,
      description: form.description,
      price: form.price ? parseInt(form.price) : null,
      category: form.category,
      duration_minutes: form.duration_minutes ? parseInt(form.duration_minutes) : null,
      active: form.active,
      image: form.image,
    };

    if (editingService) {
      updateMutation.mutate({ id: editingService.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const filteredServices = categoryFilter
    ? services.filter((s: Service) => s.category === categoryFilter)
    : services;

  const activeServicesCount = services.filter((s: Service) => s.active).length;
  const inactiveServicesCount = services.filter((s: Service) => !s.active).length;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('services.title')}</h1>
          <p className="text-gray-600">{isCarCare ? t('services.subtitleCarCare') : t('services.subtitleGarage')}</p>
        </div>
        <Button onClick={openCreateModal}>
          <Plus className="mr-2 h-4 w-4" />
          {t('services.addService')}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">{t('services.statTotal')}</p>
            <p className="text-2xl font-bold">{services.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">{t('services.statActive')}</p>
            <p className="text-2xl font-bold text-green-600">{activeServicesCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">{t('services.statInactive')}</p>
            <p className="text-2xl font-bold text-gray-400">{inactiveServicesCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="w-full sm:w-64">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('services.category')}
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as AnyServiceCategory | '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('services.allCategories')}</option>
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Services Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : filteredServices.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Wrench className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {t('services.emptyTitle')}
          </h3>
          <p className="mt-1 text-gray-500">
            {categoryFilter
              ? t('services.emptyFiltered')
              : t('services.emptyDefault')}
          </p>
          {!categoryFilter && (
            <Button onClick={openCreateModal} className="mt-4">
              <Plus className="mr-2 h-4 w-4" />
              {t('services.addService')}
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.map((service: Service) => (
            <Card
              key={service.id}
              className={`overflow-hidden ${!service.active ? 'opacity-60' : ''}`}
            >
              {/* Image */}
              <div className="h-48 bg-gray-100 relative">
                {service.image_url ? (
                  <img
                    src={service.image_url}
                    alt={service.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <ImageIcon className="h-12 w-12 text-gray-300" />
                  </div>
                )}
                {!service.active && (
                  <div className="absolute top-2 right-2">
                    <StatusBadge status="inactive" />
                  </div>
                )}
              </div>

              <CardContent className="p-4">
                {/* Category */}
                <p className="text-xs text-blue-600 font-medium uppercase tracking-wide">
                  {categories.find((c) => c.value === service.category)?.label || service.category}
                </p>

                {/* Name */}
                <h3 className="text-lg font-semibold text-gray-900 mt-1">
                  {service.name}
                </h3>

                {/* Description */}
                {service.description && (
                  <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                    {service.description}
                  </p>
                )}

                {/* Price & Duration */}
                <div className="mt-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">{t('services.price')}</p>
                    <p className="text-lg font-bold text-gray-900">
                      {service.price ? formatCurrency(service.price) : t('services.askUs')}
                    </p>
                  </div>
                  {service.duration_minutes && (
                    <div className="text-right">
                      <p className="text-xs text-gray-500">{t('services.duration')}</p>
                      <p className="text-sm font-medium text-gray-700">
                        {service.duration_minutes} min
                      </p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="mt-4 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => openEditModal(service)}
                  >
                    <Pencil className="h-4 w-4 mr-1" />
                    {t('services.edit')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => deleteMutation.mutate(service.id)}
                    isLoading={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editingService ? t('services.editService') : t('services.addService')}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={closeModal}>
              {t('services.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              isLoading={createMutation.isPending || updateMutation.isPending}
              disabled={!form.name}
            >
              {editingService ? t('services.saveChanges') : t('services.createService')}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('services.imageLabel')}
            </label>
            <div className="mt-1">
              {form.imagePreview ? (
                <div className="relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={form.imagePreview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={clearImage}
                    className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100"
                  >
                    <X className="h-4 w-4 text-gray-600" />
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-48 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition-colors"
                >
                  <ImageIcon className="h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-600">{t('services.uploadPrompt')}</p>
                  <p className="text-xs text-gray-400">{t('services.uploadHint')}</p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </div>
          </div>

          {/* Name */}
          <Input
            label={t('services.nameLabel')}
            placeholder={t('services.namePlaceholder')}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('services.category')}
            </label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value as AnyServiceCategory })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('services.descriptionLabel')}
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={t('services.descriptionPlaceholder')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Price & Duration */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label={t('services.priceLabel', { currency: uiCurrency() })}
              type="number"
              placeholder={t('services.pricePlaceholder')}
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
            />
            <Input
              label={t('services.durationLabel')}
              type="number"
              placeholder={t('services.durationPlaceholder')}
              value={form.duration_minutes}
              onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
            />
          </div>

          {/* Active Toggle */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="active"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="active" className="ml-2 text-sm text-gray-700">
              {t('services.activeToggle')}
            </label>
          </div>
        </form>
      </Modal>
    </div>
  );
}
